import type {
  PasskeyAssertion,
  PasskeyAuthenticationOptions,
  PasskeyRegistrationOptions,
  PasskeyRegistrationResponse,
} from '@paguz/contracts';
import {
  WebAuthnError,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
} from '@simplewebauthn/browser';

/**
 * Biometría del dispositivo (huella, Face ID, Windows Hello) con passkeys WebAuthn de plataforma.
 * La llave privada nunca sale del teléfono; la API solo recibe firmas. Este módulo recuerda **qué
 * credenciales se crearon en este dispositivo** (solo sus ids, no son secretos) para no pedir huella
 * en un teléfono que no tiene la passkey.
 */
const CREDENTIALS_KEY = 'paguz.web.passkeys.v1';
const OFFER_DISMISSED_KEY = 'paguz.web.passkey-offer.v1';

/** Detección rápida y síncrona: sin WebAuthn no hay nada que preguntar. */
export function webAuthnSupported(): boolean {
  return typeof globalThis.PublicKeyCredential === 'function';
}

/** ¿El dispositivo tiene un autenticador de plataforma con verificación de usuario (biometría/PIN)? */
export async function deviceHasBiometrics(): Promise<boolean> {
  if (!webAuthnSupported()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

export function localCredentialIds(): string[] {
  try {
    const raw = globalThis.localStorage.getItem(CREDENTIALS_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberCredential(credentialId: string): void {
  try {
    const ids = new Set(localCredentialIds());
    ids.add(credentialId);
    globalThis.localStorage.setItem(CREDENTIALS_KEY, JSON.stringify([...ids]));
  } catch {
    // Almacenamiento bloqueado: la próxima vez se usará el código SMS.
  }
}

/** Opciones limitadas a las passkeys del cliente que viven en este dispositivo. */
export function onThisDevice(options: PasskeyAuthenticationOptions): PasskeyAuthenticationOptions {
  const local = new Set(localCredentialIds());
  return {
    ...options,
    allowCredentials: (options.allowCredentials ?? []).filter((c) => local.has(c.id)),
  };
}

/** "Ahora no" vale por la sesión de la app: se vuelve a ofrecer la próxima vez que la abra. */
export function enrollmentOfferDismissed(): boolean {
  try {
    return globalThis.sessionStorage.getItem(OFFER_DISMISSED_KEY) === 'dismissed';
  } catch {
    return false;
  }
}

export function dismissEnrollmentOffer(): void {
  try {
    globalThis.sessionStorage.setItem(OFFER_DISMISSED_KEY, 'dismissed');
  } catch {
    // nada
  }
}

/** Muestra el diálogo de huella / Face ID y firma el reto. */
export async function signWithBiometrics(
  options: PasskeyAuthenticationOptions,
): Promise<PasskeyAssertion> {
  const assertion = await startAuthentication({
    optionsJSON: options,
  });
  return assertion as unknown as PasskeyAssertion;
}

/** Crea la passkey en el autenticador del dispositivo (pide la huella / Face ID). */
export async function createDevicePasskey(
  options: PasskeyRegistrationOptions,
): Promise<PasskeyRegistrationResponse> {
  const registration = await startRegistration({
    optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON,
  });
  return registration as unknown as PasskeyRegistrationResponse;
}

/** El cliente cerró el diálogo (o venció): no es un error, solo se ofrece el código SMS. */
export function isBiometricCancel(error: unknown): boolean {
  if (error instanceof WebAuthnError && error.code === 'ERROR_CEREMONY_ABORTED') return true;
  // `WebAuthnError` conserva el `name` del DOMException original.
  return (
    error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

/** Nombre genérico del dispositivo para la lista de passkeys (sin datos personales). */
export function deviceLabel(userAgent = globalThis.navigator.userAgent): string {
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent)) return 'iPad';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'Mac';
  if (/Windows/i.test(userAgent)) return 'Windows';
  return 'Este dispositivo';
}
